import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Mail, Clock, Send, LogOut } from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { apiClient } from "@/lib/api-client";
import { storage } from "@/lib/storage";
import { useAuth } from "@/contexts/AuthContext";

const CODE_LENGTH = 6;

interface EmailVerificationModalProps {
  isOpen: boolean;
  email: string;
  onVerified: () => void;
}

export const EmailVerificationModal: React.FC<EmailVerificationModalProps> = ({
  isOpen,
  email,
  onVerified,
}) => {
  const { toast } = useToast();
  const { user, signOut } = useAuth();
  const [digits, setDigits] = useState<string[]>(Array(CODE_LENGTH).fill(''));
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Dev mode: allow closing modal via storage flag
  // Set in browser console: storage.setItem('dev_skip_email_verification', 'true')
  // TODO: restore isDev check before merging: const skipVerification = import.meta.env.DEV && storage.getItem('dev_skip_email_verification') === 'true';
  const skipVerification = storage.getItem('dev_skip_email_verification') === 'true';

  // Check if user is verified periodically
  useEffect(() => {
    if (!isOpen) return;

    const checkVerificationStatus = async () => {
      try {
        const userData = await apiClient.get('/auth/me');
        if (userData.user?.email_verified) {
          onVerified();
        }
      } catch (error: any) {
        if (error?.message) {
          console.debug('[EmailVerificationModal] Error checking verification status:', error.message);
        }
      }
    };

    checkVerificationStatus();
    const interval = setInterval(checkVerificationStatus, 3000);
    return () => clearInterval(interval);
  }, [isOpen, onVerified]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Auto-submit when all digits filled
  const handleVerifyCode = useCallback(async (code: string) => {
    if (code.length !== CODE_LENGTH || isVerifying) return;

    setIsVerifying(true);
    try {
      const result = await apiClient.post('/auth/verify-email', {
        token: code,
        uid: user?.uuid,
      });

      if (result.success) {
        toast({
          title: "Email Verified!",
          description: "Your email has been successfully verified.",
          variant: "success",
        });
        onVerified();
      }
    } catch (error: any) {
      toast({
        title: "Verification Failed",
        description: error.message || "Invalid or expired code. Please try again.",
        variant: "destructive",
      });
      // Clear digits on failure so user can retry
      setDigits(Array(CODE_LENGTH).fill(''));
      inputRefs.current[0]?.focus();
    } finally {
      setIsVerifying(false);
    }
  }, [isVerifying, onVerified, toast, user?.uuid]);

  const handleDigitChange = (index: number, value: string) => {
    // Only allow single digit
    const digit = value.replace(/\D/g, '').slice(-1);
    
    const newDigits = [...digits];
    newDigits[index] = digit;
    setDigits(newDigits);

    // Auto-advance to next input
    if (digit && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when complete
    const fullCode = newDigits.join('');
    if (fullCode.length === CODE_LENGTH) {
      handleVerifyCode(fullCode);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (!digits[index] && index > 0) {
        // Move back and clear previous
        const newDigits = [...digits];
        newDigits[index - 1] = '';
        setDigits(newDigits);
        inputRefs.current[index - 1]?.focus();
      } else {
        const newDigits = [...digits];
        newDigits[index] = '';
        setDigits(newDigits);
      }
      e.preventDefault();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
    if (!pasted) return;

    const newDigits = Array(CODE_LENGTH).fill('');
    for (let i = 0; i < pasted.length; i++) {
      newDigits[i] = pasted[i];
    }
    setDigits(newDigits);

    // Focus appropriate input
    const nextEmpty = pasted.length < CODE_LENGTH ? pasted.length : CODE_LENGTH - 1;
    inputRefs.current[nextEmpty]?.focus();

    // Auto-submit if full
    if (pasted.length === CODE_LENGTH) {
      handleVerifyCode(pasted);
    }
  };

  const handleResendEmail = async () => {
    if (resendCooldown > 0) return;

    setIsResending(true);
    try {
      await apiClient.post('/auth/resend-verification', {
        email: email,
      });

      toast({
        title: "New Code Sent",
        description: "A new verification code has been sent to your inbox.",
        variant: "success",
      });

      // Reset digits and start cooldown
      setDigits(Array(CODE_LENGTH).fill(''));
      inputRefs.current[0]?.focus();
      setResendCooldown(60);
    } catch (error: any) {
      toast({
        title: "Failed to Resend",
        description: error.message || "Could not send verification code. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsResending(false);
    }
  };

  // Focus first input when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen && !skipVerification} onOpenChange={() => {}}>
      <DialogContent 
        className="max-w-md w-[95vw] p-0 overflow-hidden border-0 bg-transparent shadow-none [&>button]:hidden"
        aria-describedby={undefined}
      >
        <div className="relative bg-neutral-900 rounded-2xl overflow-hidden border border-neutral-700/60 shadow-[0_8px_40px_rgba(0,0,0,0.5)]">
          {/* Subtle top accent bar */}
          <div className="h-1 bg-gradient-to-r from-yellow-500/80 via-amber-400/80 to-yellow-500/80" />

          {/* Content */}
          <div className="p-6 sm:p-8">
            {/* Header */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-yellow-500/10 border border-yellow-500/20 mb-4">
                <Mail className="h-7 w-7 text-yellow-400" />
              </div>
              <h2 className="text-lg sm:text-xl font-bold text-white mb-1">
                Verify Your Email
              </h2>
              <p className="text-neutral-400 text-sm">
                We sent a 6-digit code to
              </p>
              <p className="font-semibold text-white text-base mt-1">{email}</p>
            </div>

            {/* PIN input */}
            <div className="grid grid-cols-6 gap-2 sm:gap-3 mb-2 max-w-[320px] mx-auto" onPaste={handlePaste}>
              {digits.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => { inputRefs.current[index] = el; }}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleDigitChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  disabled={isVerifying}
                  className={cn(
                    "w-full aspect-square text-center text-xl sm:text-2xl font-bold rounded-lg",
                    "bg-neutral-800/60 border border-neutral-700/50 text-white",
                    "focus:border-yellow-500/60 focus:ring-1 focus:ring-yellow-500/30 focus:outline-none",
                    "transition-colors",
                    "disabled:opacity-50",
                    digit && "border-yellow-500/40"
                  )}
                />
              ))}
            </div>

            {/* Verifying indicator */}
            {isVerifying && (
              <div className="flex items-center justify-center gap-2 text-yellow-400 text-sm mb-4">
                <Clock className="h-4 w-4 animate-spin" />
                Verifying...
              </div>
            )}

            {/* Expiry note */}
            <p className="text-center text-neutral-500 text-xs mt-3 mb-5">
              Code expires in 15 minutes
            </p>

            {/* Resend section */}
            <div className="text-center pt-4 border-t border-neutral-700/50">
              <p className="text-neutral-500 text-xs mb-2">
                Didn't receive the code?
              </p>
              {resendCooldown > 0 ? (
                <p className="text-neutral-400 text-xs">
                  Resend available in <span className="text-yellow-400 font-medium">{resendCooldown}s</span>
                </p>
              ) : (
                <button
                  className="inline-flex items-center gap-1.5 text-yellow-400 hover:text-yellow-300 text-sm font-medium transition-colors disabled:opacity-40"
                  onClick={handleResendEmail}
                  disabled={isResending}
                >
                  <Send className="h-3.5 w-3.5" />
                  {isResending ? "Sending..." : "Resend code"}
                </button>
              )}
            </div>

            {/* Note */}
            <div className="bg-neutral-800/40 rounded-lg p-3 mt-4">
              <p className="text-neutral-500 text-xs text-center leading-relaxed">
                You must verify your email before you can access your account. Check your spam folder if you don't see it. If you're still not receiving a code, please contact{' '}
                <a href="mailto:support@goldmachine.com" className="text-gold-400 hover:underline">support@goldmachine.com</a>{' '}
                as we may have hit our email send limit.
              </p>
            </div>

            {/* Log out */}
            <div className="text-center mt-4">
              <button
                className="inline-flex items-center gap-1.5 text-neutral-500 hover:text-neutral-300 text-xs transition-colors"
                onClick={signOut}
              >
                <LogOut className="h-3.5 w-3.5" />
                Log out
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
